<script setup>
import { ref, nextTick, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import CatalogProductSetsAPI from 'dashboard/api/catalogProductSets';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import PageEmptyState from 'escaleshop/shared/components/PageEmptyState.vue';

const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();

const sets = ref([]);
const loading = ref(true);
const error = ref('');
const deleting = ref(false);
const selectedSet = ref(null);
const deleteDialogRef = ref(null);

const fetchSets = async () => {
  try {
    loading.value = true;
    const { data } = await CatalogProductSetsAPI.list();
    sets.value = data.product_sets || [];
  } catch (e) {
    error.value = e.response?.data?.error || e.message;
  } finally {
    loading.value = false;
  }
};

onMounted(fetchSets);

const goToNew = () =>
  router.push({ name: 'catalog_product_set_new', params: { accountId: accountId.value } });

const goToEdit = id =>
  router.push({ name: 'catalog_product_set_edit', params: { accountId: accountId.value, setId: id } });

const openDeleteDialog = async set => {
  selectedSet.value = set;
  await nextTick();
  deleteDialogRef.value?.open();
};

const onDeleteDialogClose = () => {
  selectedSet.value = null;
};

const confirmDelete = async () => {
  if (!selectedSet.value) return;
  try {
    deleting.value = true;
    await CatalogProductSetsAPI.destroy(selectedSet.value.id);
    sets.value = sets.value.filter(s => s.id !== selectedSet.value.id);
    deleteDialogRef.value?.close();
  } catch (e) {
    error.value = e.response?.data?.error || e.message;
  } finally {
    deleting.value = false;
  }
};

const formatDate = ts =>
  ts ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(new Date(ts)) : '—';
</script>

<template>
  <div class="flex h-full w-full flex-col overflow-hidden bg-n-surface-1">
    <header class="shrink-0 px-6 lg:px-16">
      <div class="flex items-center justify-between w-full py-7 gap-2 mx-auto max-w-7xl">
        <span class="text-xl font-medium text-n-slate-12">
          {{ t('CATALOG.PRODUCT_SETS.TITLE') }}
        </span>
        <Button solid blue icon="i-lucide-plus" :label="t('CATALOG.PRODUCT_SETS.NEW')" @click="goToNew" />
      </div>
    </header>

    <div class="flex-1 overflow-y-auto scrollbar-custom px-6 lg:px-16">
      <div class="w-full mx-auto max-w-7xl pb-6">

        <div v-if="loading" class="flex items-center justify-center py-20">
          <Spinner />
        </div>

        <div v-else-if="error" class="px-4 py-3 rounded-xl bg-n-ruby-3 text-sm text-n-ruby-12">
          {{ error }}
        </div>

        <PageEmptyState
          v-else-if="!sets.length"
          icon="i-lucide-layers"
          :title="t('CATALOG.PRODUCT_SETS.EMPTY_TITLE')"
          :description="t('CATALOG.PRODUCT_SETS.EMPTY_DESCRIPTION')"
        >
          <Button solid blue icon="i-lucide-plus" :label="t('CATALOG.PRODUCT_SETS.NEW')" @click="goToNew" />
        </PageEmptyState>

        <div v-else class="flex flex-col gap-3">
          <div
            v-for="set in sets"
            :key="set.id"
            class="flex items-center justify-between gap-4 px-5 py-4 bg-n-solid-2 rounded-xl border border-n-weak"
          >
            <div class="flex flex-col gap-1 min-w-0">
              <span class="text-sm font-semibold text-n-slate-12 truncate">{{ set.name }}</span>
              <div class="flex items-center gap-2 text-xs text-n-slate-10 flex-wrap">
                <span
                  class="px-1.5 py-0.5 rounded text-[10px] font-medium"
                  :class="set.set_type === 'dynamic' ? 'bg-n-amber-3 text-n-amber-11' : 'bg-n-slate-3 text-n-slate-11'"
                >
                  {{ set.set_type === 'dynamic' ? t('CATALOG.PRODUCT_SETS.BADGE_DYNAMIC') : t('CATALOG.PRODUCT_SETS.BADGE_MANUAL') }}
                </span>
                <span>·</span>
                <span>{{ set.product_count ?? '—' }} {{ t('CATALOG.PRODUCT_SETS.TABLE.PRODUCTS').toLowerCase() }}</span>
                <span>·</span>
                <span>{{ set.variants_count ?? '—' }} {{ t('CATALOG.PRODUCT_SETS.TABLE.VARIANTS').toLowerCase() }}</span>
                <span>·</span>
                <span>{{ formatDate(set.created_at) }}</span>
              </div>
            </div>

            <div class="flex items-center gap-2 shrink-0">
              <Button
                v-tooltip.top="t('CATALOG.PRODUCT_SETS.EDIT')"
                icon="i-woot-edit-pen"
                slate
                sm
                @click="goToEdit(set.id)"
              />
              <Button
                v-tooltip.top="t('CATALOG.PRODUCT_SETS.DELETE_TITLE')"
                icon="i-woot-bin"
                slate
                sm
                class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                @click="openDeleteDialog(set)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('CATALOG.PRODUCT_SETS.DELETE_TITLE')"
      :description="t('CATALOG.PRODUCT_SETS.DELETE_DESCRIPTION', { name: selectedSet?.name ?? '' })"
      @close="onDeleteDialogClose"
    >
      <template #footer>
        <div class="flex items-center justify-between w-full gap-3">
          <Button
            variant="faded"
            color="slate"
            :label="t('CATALOG.PRODUCT_SETS.FORM.CANCEL')"
            class="w-full"
            :disabled="deleting"
            @click="deleteDialogRef?.close()"
          />
          <Button
            color="ruby"
            :label="t('CATALOG.PRODUCT_SETS.DELETE_CONFIRM')"
            class="w-full"
            :is-loading="deleting"
            :disabled="deleting"
            @click="confirmDelete"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>
